import {
  useCallback, useEffect, useRef, useState,
} from 'react';

export const useLocalFieldValue = (value) => {
  const [localValue, setLocalValue] = useState(value);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  return {
    localValue,
    setLocalValue,
  };
};

export const useBlurCommit = ({
  value,
  localValue,
  setLocalValue,
  onCommit,
  serialize = (nextValue) => JSON.stringify(nextValue),
  validate,
  savedError = null,
  onValidationChange,
}) => {
  const [error, setError] = useState(savedError);
  const errorRef = useRef(null);
  errorRef.current = error;

  const setErrorAndNotify = useCallback((msg) => {
    setError(msg);
    if (onValidationChange) onValidationChange(msg);
  }, [onValidationChange]);

  const handleBlur = useCallback(() => {
    if (validate) {
      const result = validate(localValue);
      if (!result.isValid) {
        setErrorAndNotify(result.errorMessage);
        return;
      }
      setErrorAndNotify(null);
    }
    const prevSerialized = serialize(value);
    const nextSerialized = serialize(localValue);
    if (prevSerialized !== nextSerialized) {
      onCommit(localValue, () => setLocalValue(value));
    }
  }, [localValue, onCommit, serialize, setErrorAndNotify, setLocalValue, value, validate]);

  const clearErrorIfValid = useCallback((nextValue) => {
    if (!errorRef.current || !validate) return;
    if (validate(nextValue).isValid) setErrorAndNotify(null);
  }, [setErrorAndNotify, validate]);

  return { handleBlur, error, clearErrorIfValid };
};

export const useEnterToBlur = () => useCallback((event) => {
  if (event.key === 'Enter') event.target.blur();
}, []);
